import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { SearchIcon, CheckCircleIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';

const codeData = {
  'T1015': {
    description: 'Clinic visit/encounter, all-inclusive',
    federal: {
      source: 'CMS IOM Pub. 100-04, Chapter 9',
      summary: 'Core FQHC encounter code. Must represent a qualifying face-to-face visit.',
      rules: [
        'Requires a face-to-face encounter with a core practitioner.',
        'Medicare PPS claims use specific payment codes (G0466\u2013G0470).',
        'Incident-to services are included in the all-inclusive rate.'
      ]
    },
    mac: {
      source: 'Novitas (JL) FQHC Billing Guidance \u2014 Pennsylvania',
      summary: 'Medicare-side mechanics for PA FQHCs, including the MA wrap.',
      rules: [
        'Append modifier 95 for eligible telehealth services.',
        'Medicare Advantage supplemental wrap claims bill revenue code 0519; missing plan rate on file returns reason code 37098.'
      ]
    },
    state: {
      source: 'PA FQHC/RHC PROMISe Provider Handbook & OMAP MA Bulletins',
      summary: 'Strict line-order and encounter-type modifier rules.',
      rules: [
        'Bill T1015 on the FIRST claim line; report detail services as zero-pay tracking lines (MA Bulletin, Apr. 16, 2025).',
        'Vision encounters: append pricing modifier U3 (separate vision PPS rate).',
        'Teledentistry: append modifier U9 with POS 02 (MA Bulletin, May 2, 2022).',
        'Pharmacist MTM encounters payable at PPS: bill T1015 plus the MTM code with modifier AT (MA Bulletin, Feb. 13, 2024).'
      ]
    }
  },
  '0900': {
    description: 'Behavioral Health Treatments/Services (revenue code)',
    federal: {
      source: 'CMS IOM Pub. 100-04, Chapter 9',
      summary: 'General behavioral health revenue code.',
      rules: [
        'Allowed for clinical psychologist and clinical social worker encounters.',
        'May be billed the same day as a medical visit when it is a separate, distinct encounter.'
      ]
    },
    mac: {
      source: 'Novitas (JL) FQHC Billing Guidance \u2014 Pennsylvania',
      summary: 'Same-day distinct-service documentation.',
      rules: [
        'When billed same-day with a medical visit, documentation must support a distinct encounter.'
      ]
    },
    state: {
      source: 'PA HealthChoices BH Carve-Out; FQHC PROMISe Provider Handbook',
      summary: 'BH encounters route to the county BH-MCO \u2014 never the PH-MCO.',
      rules: [
        'Philadelphia County: bill Community Behavioral Health (CBH); other counties per the county BH-MCO (e.g., Magellan).',
        'BH-MCOs must pay FQHC/RHC encounters at no less than the PPS rate (HealthChoices BH Agreement, eff. 2016).',
        'A medical encounter and a BH encounter on the same day are BOTH payable \u2014 they are different encounter types (DHS FQHC/RHC FAQ).'
      ]
    }
  }
};

const FqPaDemoCodeIntelligence = () => {
  const [searchTerm, setSearchTerm] = useState('T1015');
  const [activeCode, setActiveCode] = useState('T1015');

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      const term = searchTerm.toUpperCase();
      if (codeData[term]) {
        setActiveCode(term);
      }
    }
  };

  const data = codeData[activeCode];

  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg" color="brand.700" mb={2}>Code Intelligence Explorer</Heading>
          <Text color="gray.600">
            Demonstrating Revedy's deep regulatory knowledge across Federal, MAC, and State layers.
          </Text>
        </Box>

        <Card variant="outline" bg="white">
          <CardBody>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input 
                placeholder="Search HCPCS or Revenue Code (e.g., T1015, 0900)" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearch}
              />
            </InputGroup>
            {!codeData[searchTerm.toUpperCase()] && searchTerm.length > 3 && (
              <Text color="red.500" mt={2} fontSize="sm">Code not found in demo database. Try T1015 or 0900.</Text>
            )}
          </CardBody>
        </Card>

        {data && (
          <Box>
            <HStack mb={4}>
              <Heading size="md">{activeCode}</Heading>
              <Badge colorScheme="blue" fontSize="md">{data.description}</Badge>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {/* Federal */}
              <Card variant="outline" borderColor="blue.200">
                <CardHeader bg="blue.50" pb={2}>
                  <HStack>
                    <Icon as={InfoIcon} color="blue.500" />
                    <Heading size="sm" color="blue.700">Federal (CMS)</Heading>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>{data.federal.source}</Text>
                </CardHeader>
                <CardBody>
                  <Text fontWeight="bold" fontSize="sm" mb={3}>{data.federal.summary}</Text>
                  <List spacing={2}>
                    {data.federal.rules.map((rule, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        {rule}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>

              {/* MAC */}
              <Card variant="outline" borderColor="purple.200">
                <CardHeader bg="purple.50" pb={2}>
                  <HStack>
                    <Icon as={WarningIcon} color="purple.500" />
                    <Heading size="sm" color="purple.700">MAC (Novitas JL)</Heading>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>{data.mac.source}</Text>
                </CardHeader>
                <CardBody>
                  <Text fontWeight="bold" fontSize="sm" mb={3}>{data.mac.summary}</Text>
                  <List spacing={2}>
                    {data.mac.rules.map((rule, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        {rule}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>

              {/* State */}
              <Card variant="outline" borderColor="green.200">
                <CardHeader bg="green.50" pb={2}>
                  <HStack>
                    <Icon as={CheckCircleIcon} color="green.500" />
                    <Heading size="sm" color="green.700">State (PA DHS / OMAP)</Heading>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>{data.state.source}</Text>
                </CardHeader>
                <CardBody>
                  <Text fontWeight="bold" fontSize="sm" mb={3}>{data.state.summary}</Text>
                  <List spacing={2}>
                    {data.state.rules.map((rule, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        {rule}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default FqPaDemoCodeIntelligence;
